import { useTranslation } from "react-i18next";
import type { PodcastEpisode } from "@ckgo2/shared/types";
import { episodeDurationFromMinutes, episodeDurationToMinutes } from "@ckgo2/shared";
import { useEditPage } from "@/hooks/useEditPage";
import EditPageHeader from "@/components/common/EditPageHeader";

interface PodcastEpisodeFormData {
  title: string;
  videoUrl: string;
  thumbnailUrl: string;
  publishedAt: string;
  /** Edituje se v minutách, ukládá se v sekundách (sync plní přesnou hodnotu z API). */
  durationMinutes: number | null;
  /**
   * Uložená hodnota ze serveru — needituje se, jen se veze formulářem. Dokud se
   * minutové pole nezmění, ukládá se zpátky beze změny (`episodeDurationFromMinutes`);
   * jinak by pouhé přeuložení epizody (2712 s → 2700 s) vypadalo jako ruční zásah
   * a natrvalo by epizodu vyjmulo ze syncu (AK7).
   */
  durationSeconds: number | null;
  sortOrder: number;
  /** Unie ze sdíleného typu, ne `string` — neplatný stav neprojde už při kompilaci. */
  status: PodcastEpisode["status"];
}

export default function PodcastEpisodeEditPage() {
  const { t } = useTranslation();
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    PodcastEpisodeFormData,
    PodcastEpisode
  >({
    resourceKey: "podcast-episode",
    listKey: "podcast-episodes",
    apiPath: "/api/admin/podcast-episodes",
    defaultValues: {
      title: "",
      videoUrl: "",
      thumbnailUrl: "",
      publishedAt: new Date().toISOString().slice(0, 10),
      durationMinutes: null,
      durationSeconds: null,
      sortOrder: 0,
      status: "published",
    },
    navigateTo: "/podcast-episodes",
    transformOnLoad: (e) => ({
      title: e.title || "",
      videoUrl: e.videoUrl || "",
      thumbnailUrl: e.thumbnailUrl || "",
      publishedAt: e.publishedAt || "",
      // `??` ne `||` — nulová délka je platná hodnota, prázdné pole je null.
      durationMinutes: episodeDurationToMinutes(e.durationSeconds),
      durationSeconds: e.durationSeconds ?? null,
      sortOrder: e.sortOrder ?? 0,
      status: e.status || "published",
    }),
    transformOnSave: ({ durationMinutes, durationSeconds, ...data }) => ({
      ...data,
      thumbnailUrl: data.thumbnailUrl || null,
      publishedAt: data.publishedAt || null,
      durationSeconds: episodeDurationFromMinutes(durationMinutes, durationSeconds),
    }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/podcast-episodes"
        isNew={isNew}
        newTitle={t("content.podcastEpisodes.form.newTitle")}
        editTitle={t("content.podcastEpisodes.form.editTitle")}
        entityName={itemData?.title}
        formId="podcast-episode-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        isSuccess={saveMutation.isSuccess}
        validationErrors={errors}
      />

      <form
        id="podcast-episode-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label className="label">{t("common.fields.title")}</label>
              <input
                {...register("title", { required: t("content.shared.titleRequired") })}
                className="input"
                placeholder={t("content.podcastEpisodes.form.titlePlaceholder")}
              />
              {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>}
            </div>

            <div>
              <label className="label">{t("content.podcastEpisodes.form.videoUrlLabel")}</label>
              <input
                {...register("videoUrl", {
                  required: t("content.podcastEpisodes.form.videoUrlRequired"),
                })}
                className="input"
                placeholder="https://www.youtube.com/watch?v=..."
              />
              {errors.videoUrl && (
                <p className="mt-1 text-sm text-red-500">{errors.videoUrl.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {t("content.podcastEpisodes.form.videoUrlHint")}
              </p>
            </div>

            <div>
              <label className="label">{t("content.podcastEpisodes.form.thumbnailLabel")}</label>
              <input
                {...register("thumbnailUrl")}
                className="input"
                placeholder="https://i.ytimg.com/vi/.../hqdefault.jpg"
              />
              <p className="mt-1 text-sm text-gray-500">
                {t("content.podcastEpisodes.form.thumbnailHint")}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label className="label">{t("content.podcastEpisodes.form.publishedAtLabel")}</label>
              <input type="date" {...register("publishedAt")} className="input" />
            </div>
            <div>
              <label className="label">{t("content.podcastEpisodes.form.durationLabel")}</label>
              <input
                type="number"
                min={0}
                {...register("durationMinutes", { valueAsNumber: true })}
                className="input"
              />
            </div>
            <div>
              <label className="label">{t("common.fields.sortOrder")}</label>
              <input
                type="number"
                {...register("sortOrder", { valueAsNumber: true })}
                className="input"
              />
            </div>
            <div>
              <label className="label">{t("common.fields.status")}</label>
              <select {...register("status")} className="input">
                <option value="published">{t("common.status.published")}</option>
                <option value="draft">{t("common.status.draft")}</option>
              </select>
            </div>
          </div>

          {itemData?.source === "youtube" && (
            <div className="card">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("content.podcastEpisodes.form.syncNote")}
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
